import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Premium Loading Component
const PremiumLoading = ({ primaryColor = '#8b5cf6' }) => {
  const [dots, setDots] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => (d + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100
      }}
    >
      {/* Coffee steam animation */}
      <motion.div
        style={{
          width: '80px',
          height: '80px',
          background: `linear-gradient(135deg, ${primaryColor}44, ${primaryColor}22)`,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '40px',
          marginBottom: '24px',
          position: 'relative'
        }}
      >
        ☕
        {/* Steam particles */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              top: '-8px',
              left: `${25 + i * 15}px`,
              width: '6px',
              height: '6px',
              background: 'rgba(255,255,255,0.3)',
              borderRadius: '50%'
            }}
            animate={{
              y: [-5, -25, -5],
              opacity: [0.3, 0.6, 0.3],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              delay: i * 0.3
            }}
          />
        ))}
      </motion.div>
      
      <p style={{
        color: 'white',
        fontSize: '16px',
        fontWeight: '500',
        margin: '0 0 16px'
      }}>
        AI fincanınızı inceliyor{'.'.repeat(dots)}
      </p>
      
      {/* Premium progress dots */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: i <= dots ? primaryColor : 'rgba(255,255,255,0.2)'
            }}
            animate={{
              scale: i === dots ? [1, 1.3, 1] : 1
            }}
            transition={{ duration: 0.3 }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Partner attribution storage keys
const PARTNER_STORAGE_KEY = 'mystic_partner_attribution';
const PARTNER_EXPIRY_DAYS = 7;

// Save partner attribution to localStorage and cookie
const savePartnerAttribution = (partnerData) => {
  const attribution = {
    partner_slug: partnerData.partner_slug,
    partner_id: partnerData.partner_id,
    partner_name: partnerData.partner_name,
    campaign_id: partnerData.campaign_id,
    timestamp: Date.now(),
    expires: Date.now() + (PARTNER_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
  };
  
  // Save to localStorage
  localStorage.setItem(PARTNER_STORAGE_KEY, JSON.stringify(attribution));
  
  // Save to cookie (backup)
  const expiryDate = new Date(attribution.expires).toUTCString();
  document.cookie = `${PARTNER_STORAGE_KEY}=${encodeURIComponent(JSON.stringify(attribution))}; expires=${expiryDate}; path=/; SameSite=Lax`;
  
  console.log('🎯 Partner attribution saved:', attribution);
};

// Get partner attribution from storage
export const getPartnerAttribution = () => {
  try {
    // Try localStorage first
    const stored = localStorage.getItem(PARTNER_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      if (data.expires && data.expires > Date.now()) {
        return data;
      }
      // Expired, clean up
      localStorage.removeItem(PARTNER_STORAGE_KEY);
    }
    
    // Try cookie as backup
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === PARTNER_STORAGE_KEY && value) {
        const data = JSON.parse(decodeURIComponent(value));
        if (data.expires && data.expires > Date.now()) {
          return data;
        }
      }
    }
  } catch (e) {
    console.error('Error reading partner attribution:', e);
  }
  return null;
};

// Event type mapping: frontend short names → backend enum values
const EVENT_TYPE_MAP = {
  'qr_scan': 'partner_qr_scan',
  'landing_view': 'partner_landing_view',
  'landing_cta_clicked': 'partner_cta_click',
  'cta_click': 'partner_cta_click',
  'signup_started': 'partner_signup_started',
  'signup_completed': 'partner_signup_completed',
  'gift_redeemed': 'partner_gift_redeemed',
  'reading_started': 'partner_reading_started',
  'reading_completed': 'partner_reading_completed',
  'story_shared': 'partner_story_shared'
};

// Track analytics event
const trackEvent = async (eventType, partnerId, metadata = {}) => {
  try {
    // Map short event type to backend enum format
    const mappedEventType = EVENT_TYPE_MAP[eventType] || eventType;
    
    await axios.post(`${API_URL}/api/growth-hub/events/track`, null, {
      params: {
        partner_id: partnerId,
        event_type: mappedEventType,
        metadata: JSON.stringify(metadata)
      }
    });
  } catch (e) {
    console.log('Event tracking failed:', e);
  }
};

const GiftLanding = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [landingData, setLandingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);

  // Get QR/branch params from URL
  const branchId = searchParams.get('branch');
  const qrId = searchParams.get('qr');
  const isQrScan = !!qrId;

  useEffect(() => {
    if (slug) {
      fetchLandingData();
    }
  }, [slug]);

  const fetchLandingData = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/growth-hub/landing/${slug}`);
      setLandingData(data.landing);
      
      // Save partner attribution
      savePartnerAttribution({
        ...data.landing,
        branch_id: branchId,
        qr_id: qrId
      });
      
      // Track event - differentiate QR scan from direct landing
      if (isQrScan) {
        trackEvent('qr_scan', data.landing.partner_id, { branch_id: branchId, qr_id: qrId });
      }
      trackEvent('landing_view', data.landing.partner_id, { branch_id: branchId, qr_id: qrId, is_qr: isQrScan });
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch landing data:', err);
      setError(err.response?.status === 404 ? 'Partner bulunamadı' : 'Bir hata oluştu');
      setLoading(false);
    }
  };

  const handleStartJourney = () => {
    if (landingData?.privacy?.consent_required && !consentChecked) {
      alert('Devam etmek için gizlilik koşullarını kabul etmeniz gerekiyor.');
      return;
    }
    
    // Track CTA click
    trackEvent('landing_cta_clicked', landingData.partner_id, { branch_id: branchId, qr_id: qrId });
    
    // Navigate to main app with auth modal
    navigate('/?showAuth=true&source=partner');
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: landingData?.theme?.background || 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
          style={{ fontSize: '64px' }}
        >
          {landingData?.theme?.logo_placeholder || '🔮'}
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error || !landingData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>😔</div>
          <h2 style={{ margin: '0 0 8px' }}>{error || 'Partner bulunamadı'}</h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>
            Bu bağlantı geçersiz veya süresi dolmuş olabilir.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Ana Sayfaya Git
          </button>
        </div>
      </div>
    );
  }

  // Get theme colors
  const theme = landingData.theme || {};
  const landing = landingData.landing || {};
  const privacy = landingData.privacy || {};
  
  const primaryColor = theme.primary_color || '#8b5cf6';
  const secondaryColor = theme.secondary_color || '#6366f1';
  const accentColor = theme.accent_color || '#c084fc';
  const bgGradient = theme.background || 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)';
  
  const hasBenefits = landingData.welcome_credits > 0 || 
                      landingData.premium_trial_days > 0 || 
                      landingData.first_reading_free || 
                      landingData.discount_percent > 0;

  return (
    <div style={{
      minHeight: '100vh',
      background: bgGradient,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `radial-gradient(circle at 50% 30%, ${primaryColor}22 0%, transparent 50%)`,
        pointerEvents: 'none'
      }} />
      
      {/* Stars Animation */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: '3px',
            height: '3px',
            background: 'white',
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            opacity: 0.2
          }}
          animate={{ opacity: [0.2, 0.6, 0.2] }}
          transition={{ repeat: Infinity, duration: 2 + Math.random() * 2, delay: Math.random() }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          maxWidth: '420px',
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
          paddingTop: '20px'
        }}
      >
        {/* Co-branding Header */}
        {theme.co_branding && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px',
            padding: '12px 20px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <span style={{ fontSize: '24px' }}>{theme.logo_placeholder || '☕'}</span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '20px' }}>×</span>
            <span style={{ fontSize: '24px' }}>🔮</span>
            <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
              {landingData.partner_name}
            </span>
          </div>
        )}

        {/* Main Logo/Icon */}
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ repeat: Infinity, duration: 3 }}
          style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 24px',
            background: `linear-gradient(135deg, ${primaryColor}44, ${secondaryColor}33)`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            boxShadow: `0 0 60px ${primaryColor}44`
          }}
        >
          {theme.logo_placeholder || '☕'}
        </motion.div>

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {/* Hero Title - Premium */}
          <h1 style={{
            color: 'white',
            fontSize: 'clamp(24px, 6vw, 32px)',
            fontWeight: '700',
            margin: '0 0 16px',
            lineHeight: 1.2,
            letterSpacing: '-0.5px'
          }}>
            {theme.hero_text || landing.title || landingData.custom_message || (
              <>
                <span style={{ display: 'block' }}>☕ {landingData.partner_name} x MysticAI</span>
              </>
            )}
          </h1>
          
          {/* Hero Subtitle - Premium */}
          <div style={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: 'clamp(15px, 4vw, 18px)',
            margin: '0 0 12px',
            lineHeight: 1.6,
            fontWeight: '500'
          }}>
            {theme.hero_subtitle || landing.subtitle || (
              <>
                <span style={{ display: 'block', marginBottom: '4px' }}>Kahvenizi bitirin.</span>
                <span style={{ display: 'block', color: accentColor }}>Gerisini yapay zekâ yorumlasın.</span>
              </>
            )}
          </div>
          
          {/* How it works - Quick steps */}
          <div style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '14px',
            margin: '0 0 24px',
            lineHeight: 1.8
          }}>
            <span style={{ display: 'block' }}>📱 QR kodu okutun</span>
            <span style={{ display: 'block' }}>📷 3 fotoğraf yükleyin</span>
            <span style={{ display: 'block' }}>✨ Kişiye özel AI kahve falınızı alın</span>
          </div>
        </motion.div>

        {/* Benefits Card */}
        {hasBenefits && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${primaryColor}44`,
              borderRadius: '20px',
              padding: '20px',
              marginBottom: '24px'
            }}
          >
            <h3 style={{ 
              color: accentColor, 
              fontSize: '14px', 
              margin: '0 0 16px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              🎁 Hoş Geldin Hediyesi
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {landingData.welcome_credits > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>💎</span>
                  <span style={{ color: 'white', fontSize: '15px' }}>
                    <strong>{landingData.welcome_credits} Kredi</strong> hediye
                  </span>
                </div>
              )}
              
              {landingData.first_reading_free && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>✨</span>
                  <span style={{ color: 'white', fontSize: '15px' }}>
                    <strong>İlk fal</strong> ücretsiz
                  </span>
                </div>
              )}
              
              {landingData.premium_trial_days > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>👑</span>
                  <span style={{ color: 'white', fontSize: '15px' }}>
                    <strong>{landingData.premium_trial_days} gün</strong> premium deneme
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* How It Works */}
        {landing.how_it_works && landing.how_it_works.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px'
            }}
          >
            <h4 style={{ color: 'white', fontSize: '14px', margin: '0 0 16px', fontWeight: '600' }}>
              Nasıl Çalışır?
            </h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              {landing.how_it_works.map((step, index) => (
                <div key={index} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{step.icon}</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', lineHeight: 1.3 }}>
                    {step.text}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Privacy Consent (if required) */}
        {privacy.consent_required && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '20px',
            textAlign: 'left'
          }}>
            <input
              type="checkbox"
              id="consent"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              style={{
                width: '20px',
                height: '20px',
                marginTop: '2px',
                accentColor: primaryColor
              }}
            />
            <label htmlFor="consent" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', lineHeight: 1.5 }}>
              {privacy.kvkk_text || 'Kişisel verilerimin KVKK kapsamında işlenmesini kabul ediyorum.'}
            </label>
          </div>
        )}

        {/* CTA Button - Premium */}
        <motion.button
          whileHover={{ scale: 1.03, boxShadow: `0 15px 50px ${primaryColor}55` }}
          whileTap={{ scale: 0.97 }}
          onClick={handleStartJourney}
          data-testid="landing-cta-button"
          style={{
            width: '100%',
            padding: '20px 32px',
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: 'clamp(16px, 4vw, 20px)',
            fontWeight: '700',
            boxShadow: `0 10px 40px ${primaryColor}44`,
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'all 0.3s ease'
          }}
        >
          <span>☕</span>
          <span>{theme.cta_text || 'Kahve Falını Başlat'}</span>
        </motion.button>

        {/* Demo Disclaimer */}
        {landingData.is_demo && theme.footer_disclaimer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '20px'
            }}
          >
            <p style={{ 
              color: '#fbbf24', 
              fontSize: '11px', 
              margin: 0,
              lineHeight: 1.4
            }}>
              ⚠️ {theme.footer_disclaimer}
            </p>
          </motion.div>
        )}

        {/* Footer - Premium */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            paddingTop: '16px'
          }}
        >
          {/* Demo Partner Badge */}
          {landingData.is_demo && (
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '8px 16px',
              marginBottom: '4px'
            }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Demo Partner Experience • MysticAI tarafından hazırlanmıştır
              </span>
            </div>
          )}
          
          {/* Powered By */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>🔮</span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
              AI Experience powered by <strong style={{ color: 'rgba(255,255,255,0.6)' }}>MysticAI</strong>
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default GiftLanding;
